import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { CLDR, CLDRFramework } from '@phensley/cldr';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import { Currency } from '../../components/currency-selector/currency-country-codes';
import { DEFAULT_SETTINGS } from '../../../features/user-settings/state/user-setting.default';
import { formatCurrency } from '@angular/common';
// We load the CLDR data asynchronously from node_modules folder (added to dist folder during build) because we don't want to load externally hosted data into our app, it is however possible to load them from a cdn if we trust the source. See docs for more info https://phensley.github.io/cldr-engine/docs/en/api-cldrframework.
const asyncLoader = async (language) =>
    await import(`@phensley/cldr/packs/${language}.json`);

export const framework = new CLDRFramework({ asyncLoader });

/** Service to load CLDR (Common Locale Data Repository) data for the current locale.
 */
@Injectable({ providedIn: 'root' })
export class CLDRService {
    constructor(private http: HttpClient, private _userSettingsQuery: UserSettingsQuery) {
        this.cldr$.subscribe();
    }

    CLDR: CLDR;
    cldr$ = this._userSettingsQuery.selectGlobalSetting$('LOCALE').pipe(
        switchMap((locale) => {
            const langId = locale.substring(0, locale.indexOf('-')) || locale;
            return framework.getAsync(langId);
        }),
        tap((cldr) => {
            this.CLDR = cldr;
        }),
        shareReplay(1)
    );

    formatCurrency = (
        number: number,
        currency: Currency = this._userSettingsQuery.getGlobalSetting('BASE_CURRENCY'),
        locale = this._userSettingsQuery.getGlobalSetting('LOCALE'),
        digits = this._userSettingsQuery.getGlobalSetting('DIGITS_LARGE')
    ) => {
        return formatCurrency(
            number,
            locale,
            currency,
            currency,
            '1.' + digits.join('-')
        );
    };

    formatShortCurrency = (
        number: number,
        currency: Currency = this._userSettingsQuery.getGlobalSetting('BASE_CURRENCY'),
        display = this._userSettingsQuery.getGlobalSetting('CURRENCY_SYMBOL'),
        digits = this._userSettingsQuery.getGlobalSetting('DIGITS_SMALL'),
        cldr: CLDR = this.CLDR
    ) => {
        // determine number of digits
        const numberOfDigits = (Math.log(Math.abs(number ?? 0)) * Math.LOG10E + 1) | 0;

        // only use short format for numbers with 5 or more digits
        const parts = cldr.Numbers.formatCurrencyToParts(number ?? 0, currency, {
            style: numberOfDigits > 4 ? 'short' : 'symbol',
            group: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: +digits[1]
        });

        // combine parts to a string
        return parts.reduce((acc, part) => {
            let value = part.value;

            // replace symbol with currency code
            if (part.type === 'currency' && display === 'code') {
                value = currency;
            }
            return acc + value;
        }, '');
    };

    formatShortNumber = (
        number: number,
        digits = this._userSettingsQuery.getGlobalSetting('DIGITS_SMALL'),
        cldr: CLDR = this.CLDR
    ) => {
        // determine number of digits
        const numberOfDigits = (Math.log(Math.abs(number ?? 0)) * Math.LOG10E + 1) | 0;

        // only use short format for numbers with 5 or more digits
        const parts = cldr.Numbers.formatDecimalToParts(number ?? 0, {
            style: numberOfDigits > 4 ? 'short' : 'decimal',
            group: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: +digits[1]
        });

        // combine parts to a string
        return parts.reduce((acc, part) => {
            let value = part.value;
            return acc + value;
        }, '');
    };
}
